import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'bottom-right',
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast = { ...toast, id };
      setToasts((prev) => [...prev, newToast]);

      const duration = toast.duration ?? 5000;
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  const success = useCallback(
    (message: string, title?: string) => {
      addToast({ type: 'success', message, title });
    },
    [addToast]
  );

  const error = useCallback(
    (message: string, title?: string) => {
      addToast({ type: 'error', message, title });
    },
    [addToast]
  );

  const warning = useCallback(
    (message: string, title?: string) => {
      addToast({ type: 'warning', message, title });
    },
    [addToast]
  );

  const info = useCallback(
    (message: string, title?: string) => {
      addToast({ type: 'info', message, title });
    },
    [addToast]
  );

  // Use inline styles for guaranteed positioning
  const getContainerStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyle, top: '1rem', right: '1rem' };
      case 'top-left':
        return { ...baseStyle, top: '1rem', left: '1rem' };
      case 'bottom-right':
        return { ...baseStyle, bottom: '1rem', right: '1rem' };
      case 'bottom-left':
        return { ...baseStyle, bottom: '1rem', left: '1rem' };
      default:
        return { ...baseStyle, bottom: '1rem', right: '1rem' };
    }
  };

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div style={getContainerStyle()}>
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const iconColors: Record<ToastType, string> = {
    success: '#059669',
    error: '#DC2626',
    warning: '#D97706',
    info: '#2563EB',
  };

  const backgrounds: Record<ToastType, string> = {
    success: '#F0FDF4',
    error: '#FEF2F2',
    warning: '#FFFBEB',
    info: '#EFF6FF',
  };

  const borders: Record<ToastType, string> = {
    success: '#BBF7D0',
    error: '#FECACA',
    warning: '#FDE68A',
    info: '#BFDBFE',
  };

  const IconComponent: React.FC<{ type: ToastType }> = ({ type }) => {
    const color = iconColors[type];
    const style = { width: 20, height: 20, color, flexShrink: 0 };
    switch (type) {
      case 'success': return <CheckCircle style={style} />;
      case 'error':   return <XCircle style={style} />;
      case 'warning': return <AlertTriangle style={style} />;
      default:        return <Info style={style} />;
    }
  };

  const toastStyle: React.CSSProperties = {
    pointerEvents: 'auto',
    backgroundColor: backgrounds[toast.type],
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: `2px solid ${borders[toast.type]}`,
    padding: '1rem',
    marginBottom: '0.5rem',
    minWidth: '300px',
    maxWidth: '28rem',
    animation: 'slideIn 0.3s ease-out',
  };

  const contentStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
  };

  const iconStyle: React.CSSProperties = {
    flexShrink: 0,
    width: '1.5rem',
    height: '1.5rem',
    fontSize: '1.25rem',
    lineHeight: '1.5rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: iconColors[toast.type],
  };

  const bodyStyle: React.CSSProperties = {
    flex: 1,
  };

  const titleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
  };

  const messageStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: '#475569',
    lineHeight: '1.25rem',
  };

  const titles: Record<ToastType, string> = {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
  };

  const closeStyle: React.CSSProperties = {
    flexShrink: 0,
    color: '#94a3b8',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    lineHeight: '1',
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div style={toastStyle}>
      <div style={contentStyle}>
        <IconComponent type={toast.type} />
        <div style={bodyStyle}>
          <p style={titleStyle}>{toast.title || titles[toast.type]}</p>
          <p style={messageStyle}>{toast.message}</p>
        </div>
        <button
          style={closeStyle}
          onClick={() => onDismiss(toast.id)}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#475569')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
          aria-label="Dismiss notification"
        >
          <X style={{ width: 16, height: 16 }} />
        </button>
      </div>
    </div>
  );
};